// Session context for the BARK SPA. (BAR-40 route-tree overhaul)
//
// The chat / streaming / reports / research orchestration bundle that the
// top-level App owns today and currently prop-drills (~90 props) into
// MainApplicationUI. Exposed to the route tree via two contexts:
//   - SessionActionsContext: stable handlers (useCallback'd) — consumed by
//     deep components (chat area, sidebar, report views, landing).
//   - SessionStateContext: the volatile values (streaming tokens, chat map,
//     research progress). Only the chat panel + workspace subscribe, so
//     token streaming never re-renders the non-project views.
//
// Domain/server state (activeProject, dictWorkspace, workspaceMode, …) is
// deliberately NOT here — it stays in the zustand projectStore and views
// read it via selectors, to avoid double-sourcing.
//
// Stage 0: contracts + typed hooks only; thin value pass-through provider.
import React, { createContext, useContext } from 'react';

// Loosely-typed legacy shapes kept pragmatic; tightened opportunistically as
// views are extracted. `any` here mirrors the monolith's existing typing.
export interface SessionState {
  // Chat
  chats: Record<string, any>;
  activeChatId: number | null;
  // Streaming / workflow
  streamingStatus: string | null;
  streamingResponse: string;
  streamingThinking: string;
  activityLog: Array<{ type: string; content: string; timestamp: number }>;
  isLoading: boolean;
  activeToolCall: string | null;
  pendingQuestion: any | null;
  activeWorkers: Record<string, any>;
  workflowPhase: string | null;
  workflowError: string | null;
  hasReconnectedSession: boolean;
  tokenUsage: { inputTokens: number; outputTokens: number; totalTokens: number };
  // Reports
  openReports: Record<string, any>;
  activeReportId: string | null;
  currentReport: any | null;
  /** Legacy top-level view marker (mostly 'welcome'); kept for compatibility. */
  activeView: string;
  /** Institution regulatory settings (consumed by the settings view). */
  regulatorySettings: any;
  // Research
  researchMission: any | null;
  researchProgress: any;
  activeResearchTrackingId: string | null;
  // Build handoff
  handoffPackage: any | null;
  showHandoffPanel: boolean;
  isHandoffOpen: boolean;
  // Project-open overlay gate (read by ProjectGate)
  isProjectOpening: boolean;
  // Chat pre-seed (ORDER-SENSITIVE: set before navigation so the chat mount
  // sees a non-null initialInput + autoSend in the same render).
  prefillChatInput: string | null;
  autoSendPrefill: boolean;
}

export interface SessionActions {
  handleSendMessage: (text: string, mode?: string, isComment?: boolean, opts?: any) => void;
  handleNewQuery: (...args: any[]) => void;
  onAnswerQuestion: (answer: string) => void;
  onCancelWorkflow: () => void;
  onSendComment?: (...args: any[]) => void;
  handleReconnectToStream: (projectId: string) => void;
  handleRestoreResearch: (projectId: string) => Promise<void> | void;
  resetEphemeralProjectState: () => void;
  resetChatState: (projectId: string) => void;
  handleCloseReport?: (...args: any[]) => void;
  handleSwitchToReport?: (...args: any[]) => void;
  handleDeleteChat: (...args: any[]) => void;
  handleRenameChat: (...args: any[]) => void;
  handleSelectQuery: (...args: any[]) => void;
  handleSettingsChange: (...args: any[]) => void;
  // NOTE: the chat pre-seed setters (setPrefillChatInput/setAutoSendPrefill) and
  // handleSelectProject moved to ShellUiActions (BAR-40 S4) — they mutate
  // MainApplicationUI-local state, so MainApplicationUI provides them, not App.
  // S3a: remaining MainApplicationUI handlers/setters threaded for the shell.
  onServiceSelect?: (...args: any[]) => void;
  getResearchProgress?: (...args: any[]) => any;
  setIsLoading?: (v: boolean) => void;
  setStreamingResponse?: (v: string) => void;
  setStreamingThinking?: (v: string) => void;
  setHandoffPackage?: (v: any) => void;
  setShowHandoffPanel?: (v: boolean) => void;
  setIsHandoffOpen?: (v: boolean) => void;
  setActiveView?: (v: string) => void;
}

const SessionStateContext = createContext<SessionState | undefined>(undefined);
const SessionActionsContext = createContext<SessionActions | undefined>(undefined);

export function SessionProvider({
  state,
  actions,
  children,
}: {
  state: SessionState;
  actions: SessionActions;
  children: React.ReactNode;
}) {
  return (
    <SessionActionsContext.Provider value={actions}>
      <SessionStateContext.Provider value={state}>{children}</SessionStateContext.Provider>
    </SessionActionsContext.Provider>
  );
}

export function useSessionState(): SessionState {
  const ctx = useContext(SessionStateContext);
  if (ctx === undefined) throw new Error('useSessionState must be used within <SessionProvider>');
  return ctx;
}

export function useSessionActions(): SessionActions {
  const ctx = useContext(SessionActionsContext);
  if (ctx === undefined) throw new Error('useSessionActions must be used within <SessionProvider>');
  return ctx;
}
